/**
 * SelectableList Component
 * 
 * A list component with checkbox selection supporting:
 * - Single click selection
 * - Shift+click range selection
 * - Cmd/Ctrl+A select all
 * - Visual selection states
 */

import {
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
  type MouseEvent as ReactMouseEvent
} from 'react';
import { Check, Minus } from 'lucide-react';
import type { SelectableListProps, ItemRenderProps } from './types';

// ============================================================================
// SelectableListItem Component
// ============================================================================

interface SelectableListItemProps {
  id: string;
  selected: boolean;
  onToggle: (e: ReactMouseEvent) => void;
  children: ReactNode;
  disabled?: boolean;
}

function SelectableListItem({ 
  id, 
  selected, 
  onToggle, 
  children, 
  disabled = false 
}: SelectableListItemProps) {
  return (
    <div
      className={`flex items-start gap-3 p-3  border transition-all
                  ${selected 
                    ? 'bg-primary-50 border-primary-300 ring-1 ring-primary-200' 
                    : 'bg-[var(--bg-raised)] border-[var(--rule)] hover:border-[var(--rule)] hover:bg-[var(--surface)]'
                  }
                  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={disabled ? undefined : onToggle}
      data-item-id={id}
    >
      {/* Checkbox */}
      <div 
        className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center
                    transition-colors mt-0.5
                    ${selected 
                      ? 'bg-primary-600 border-primary-600' 
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-400'
                    }`}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) onToggle(e);
        }}
      >
        {selected && <Check className="h-3 w-3 text-white" />}
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {children}
      </div>
    </div>
  );
}

// ============================================================================
// Select All Checkbox
// ============================================================================

interface SelectAllCheckboxProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function SelectAllCheckbox({ 
  selectedCount, 
  totalCount, 
  onSelectAll, 
  onDeselectAll 
}: SelectAllCheckboxProps) {
  const isAllSelected = selectedCount === totalCount && totalCount > 0;
  const isIndeterminate = selectedCount > 0 && selectedCount < totalCount;
  
  const handleClick = () => {
    if (isAllSelected || isIndeterminate) {
      onDeselectAll();
    } else {
      onSelectAll();
    }
  };
  
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleClick}
        className={`w-5 h-5 rounded border-2 flex items-center justify-center
                    transition-colors
                    ${isAllSelected || isIndeterminate
                      ? 'bg-primary-600 border-primary-600' 
                      : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-400'
                    }`}
      >
        {isAllSelected && <Check className="h-3 w-3 text-white" />}
        {isIndeterminate && <Minus className="h-3 w-3 text-white" />}
      </button>
      <span className="text-sm text-[var(--text-sec)]">
        {selectedCount > 0 
          ? `${selectedCount} of ${totalCount} selected` 
          : `${totalCount} items`
        }
      </span>
    </div>
  );
}

// ============================================================================
// SelectableList Component
// ============================================================================

export function SelectableList<T>({
  items,
  getId,
  renderItem,
  selectedIds,
  onSelectionChange,
  className = '',
  listStyle = 'cards',
}: SelectableListProps<T>) {
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Get ordered item IDs
  const itemIds = items.map(getId);
  
  // Handle Cmd/Ctrl+A
  useEffect(() => {
    const handleKeyDown = (e: globalThis.KeyboardEvent) => {
      // Only handle if focus is within the list
      if (!containerRef.current?.contains(document.activeElement)) return;
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        if (selectedIds.size === items.length) {
          onSelectionChange(new Set());
        } else {
          onSelectionChange(new Set(itemIds));
        }
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [items, itemIds, selectedIds.size, onSelectionChange]);
  
  // Handle item toggle with shift-click range selection
  const handleToggle = useCallback((id: string, e: ReactMouseEvent) => {
    const isShiftClick = e.shiftKey;
    const isCtrlClick = e.metaKey || e.ctrlKey;
    
    if (isShiftClick && lastSelectedId) {
      // Range selection
      const startIdx = itemIds.indexOf(lastSelectedId);
      const endIdx = itemIds.indexOf(id);
      
      if (startIdx !== -1 && endIdx !== -1) {
        const minIdx = Math.min(startIdx, endIdx);
        const maxIdx = Math.max(startIdx, endIdx);
        const rangeIds = itemIds.slice(minIdx, maxIdx + 1);
        
        const newSelected = new Set(selectedIds);
        rangeIds.forEach(rangeId => newSelected.add(rangeId));
        onSelectionChange(newSelected);
        setLastSelectedId(id);
        return;
      }
    }
    
    if (isCtrlClick) {
      // Toggle single item (add to selection)
      const newSelected = new Set(selectedIds);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      onSelectionChange(newSelected);
      setLastSelectedId(id);
      return;
    }
    
    // Normal click - toggle single item
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    onSelectionChange(newSelected);
    setLastSelectedId(id);
  }, [itemIds, lastSelectedId, selectedIds, onSelectionChange]);
  
  // Select all
  const handleSelectAll = useCallback(() => {
    onSelectionChange(new Set(itemIds));
  }, [itemIds, onSelectionChange]);
  
  // Deselect all
  const handleDeselectAll = useCallback(() => {
    onSelectionChange(new Set());
    setLastSelectedId(null);
  }, [onSelectionChange]);
  
  const makeItemProps = (id: string, isSelected: boolean): ItemRenderProps => ({
    selected: isSelected,
    onSelect: () => {
      const newSelected = new Set(selectedIds);
      newSelected.add(id);
      onSelectionChange(newSelected);
    },
    onToggle: () => {
      const newSelected = new Set(selectedIds);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }
      onSelectionChange(newSelected);
    },
  });

  // ── Rows style: unified container with divide-y ───────────────────────────
  if (listStyle === 'rows') {
    return (
      <div ref={containerRef} className={`bg-[var(--bg-raised)] border border-[var(--rule)]  overflow-hidden ${className}`} tabIndex={0}>
        {/* Select all header */}
        <div className="flex items-center gap-3 px-4 py-2.5 border-b border-[var(--rule)] bg-[var(--surface)]/60">
          <SelectAllCheckbox
            selectedCount={selectedIds.size}
            totalCount={items.length}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
          />
        </div>

        {/* Rows */}
        <div className="divide-y divide-[var(--rule)]">
          {items.map((item) => {
            const id = getId(item);
            const isSelected = selectedIds.has(id);
            return (
              <div
                key={id}
                data-item-id={id}
                onClick={(e) => handleToggle(id, e)}
                className={`flex items-center gap-3 px-3 py-3 transition-colors cursor-pointer ${
                  isSelected ? 'bg-primary-50' : 'hover:bg-[var(--surface)]/60'
                }`}
              >
                {/* Checkbox */}
                <div
                  className={`flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-primary-600 border-primary-600' : 'border-[var(--rule)] bg-[var(--bg-raised)] hover:border-primary-400'
                  }`}
                  onClick={(e) => { e.stopPropagation(); handleToggle(id, e); }}
                >
                  {isSelected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                </div>
                <div className="flex-1 min-w-0">
                  {renderItem(item, makeItemProps(id, isSelected))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Cards style: individual bordered cards (default) ─────────────────────
  return (
    <div 
      ref={containerRef} 
      className={`space-y-2 ${className}`}
      tabIndex={0}
    >
      {/* Select all header */}
      <div className="flex items-center justify-between px-1 py-2 border-b border-[var(--rule)]">
        <SelectAllCheckbox
          selectedCount={selectedIds.size}
          totalCount={items.length}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
        />
      </div>
      
      {/* Items */}
      <div className="space-y-2">
        {items.map((item) => {
          const id = getId(item);
          const isSelected = selectedIds.has(id);
          return (
            <SelectableListItem
              key={id}
              id={id}
              selected={isSelected}
              onToggle={(e) => handleToggle(id, e)}
            >
              {renderItem(item, makeItemProps(id, isSelected))}
            </SelectableListItem>
          );
        })}
      </div>
    </div>
  );
}

export default SelectableList;
